import { useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { registrationApi } from "../api/registration";
import { useAuth } from "../auth/AuthContext";
import { Field, ProblemAlert } from "../components/ui";

/** "Create an account" (SELF_REGISTRATION=true): makes an applicant account and emails a verification link. */
export function RegisterPage() {
  const { config } = useAuth();
  const [values, setValues] = useState({
    email: "",
    displayName: "",
    password: "",
    confirm: "",
  });
  const [mismatch, setMismatch] = useState(false);
  const register = useMutation({
    mutationFn: () =>
      registrationApi.register({
        email: values.email,
        displayName: values.displayName,
        password: values.password,
      }),
  });
  const set = (k: keyof typeof values) => (e: { target: { value: string } }) =>
    setValues((v) => ({ ...v, [k]: e.target.value }));

  if (config && (config.provider !== "local" || !config.selfRegistration))
    return <Navigate to="/login" replace />;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setMismatch(values.password !== values.confirm);
    if (values.password === values.confirm) register.mutate();
  }

  return (
    <main className="login">
      <section className="login__card" aria-labelledby="register-title">
        <p className="brand">RegiGov</p>
        <h1 id="register-title">Create an account</h1>
        {register.data ? (
          <>
            <p role="status" className="notice">
              {register.data.message}
            </p>
            <p className="muted">
              The link lasts 24 hours. You can sign in with your email address
              and password once you have opened it.
            </p>
            <p>
              <Link to="/login">Back to sign in</Link>
            </p>
          </>
        ) : (
          <form onSubmit={onSubmit} className="form">
            <p className="muted">
              For organisations applying to the register. You sign in with your
              email address.
            </p>
            <Field label="Email address">
              <input
                name="email"
                type="email"
                autoComplete="email"
                value={values.email}
                onChange={set("email")}
                required
                disabled={register.isPending}
              />
            </Field>
            <Field label="Your name">
              <input
                name="displayName"
                autoComplete="name"
                value={values.displayName}
                onChange={set("displayName")}
                required
                disabled={register.isPending}
              />
            </Field>
            <Field label="Password" hint="At least 12 characters">
              <input
                name="password"
                type="password"
                autoComplete="new-password"
                minLength={12}
                value={values.password}
                onChange={set("password")}
                required
                disabled={register.isPending}
              />
            </Field>
            <Field label="Confirm password">
              <input
                name="confirm"
                type="password"
                autoComplete="new-password"
                value={values.confirm}
                onChange={set("confirm")}
                required
                disabled={register.isPending}
              />
            </Field>
            {mismatch && (
              <p role="alert" className="error">
                The passwords do not match
              </p>
            )}
            <ProblemAlert error={register.error} />
            <button type="submit" disabled={register.isPending}>
              {register.isPending ? "Creating…" : "Create account"}
            </button>
            <p>
              <Link to="/login">I already have an account</Link>
            </p>
          </form>
        )}
      </section>
    </main>
  );
}
